<template>
  <div
    :class="{
      'select-fi': true,
      'select-fi--small': small,
      'select-fi--large': large,
      [`select-fi--large-from-${largeFrom}`]: largeFrom,
      'select-fi--light': light,
      'select-fi--transparent': transparent,
      'select-fi--disabled': disabled,
      'select-fi--options-overlap': optionsOverlap,
      'select-fi--has-error': hasError,
      'select-fi--open': open,
      'select-fi--inline-open': mobileInline && !isSM && open,
    }"
    data-test="select"
    :data-test-2="dataTest2"
  >
    <div
      ref="button"
      class="select-fi__content"
    >
      <button-fi
        :class="{
          'select-fi__button': true,
          'select-fi__button--open': open,
          'select-fi__button--disabled': disabled,
        }"
        :transparent="transparent"
        :disabled="disabled"
        type="button"
        data-test="select-button"
        @click.prevent.stop="onToggleOpen"
      >
        <div class="select-fi__text">
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="select-fi__name"
            data-test="select-name"
            v-html="name"
          />
          <!-- eslint-enable vue/no-v-html -->

          <div
            v-if="!noMobileSelection"
            class="select-fi__selection"
          >
            <slot name="selectionMobile">
              <template v-for="element in elements">
                <template v-if="element.selected">
                  {{ element.name || element.text }}
                </template>
              </template>
            </slot>
          </div>
        </div>
        <div
          :class="{
            'select-fi__chevron': true,
            'select-fi__chevron--mobile': mobileChevron,
          }"
        >
          <icon-fi
            v-if="!open || !chevronOpen"
            :rotate="open ? '180' : '0'"
            :icon="chevronClosed"
          />
          <icon-fi
            v-else
            :icon="chevronOpen"
          />
        </div>
        <div
          v-if="!mobileChevron"
          class="select-fi__arrow"
        >
          <icon-fi icon="arrow-right" />
        </div>
      </button-fi>
    </div>

    <div
      v-if="mobileInline && !isSM && isMounted"
      class="select-fi__popout__mobile-inline-wrapper"
    >
      <transition>
        <div
          v-if="internalOpen"
          class="select-fi__popout__mobile-inline"
        >
          <div class="select-fi__popout__mobile-inline-wrapper">
            <slot>
              <facet-list-fi
                :name="name || ''"
                :elements="elements || []"
                :multiselect="multiselect"
                :required="required"
                @change="onChange"
                @close="close"
                @clear-selection="$emit('clear-selection')"
              />
            </slot>
          </div>
        </div>
      </transition>
    </div>
    <teleport
      v-else-if="isMounted && internalOpen"
      to="body"
    >
      <div
        :style="wrapperStyle"
        :class="{
          'select-fi__popout-wrapper': true,
          'select-fi__popout-wrapper--open-top': openTop && isSM,
          ...wrapperClasses,
        }"
      >
        <div
          ref="popout"
          v-click-outside="closeIsSM"
          class="select-fi__popout"
          :class="{ 'select-fi__popout--leaving': leaving }"
        >
          <slot>
            <facet-list-fi
              :name="name || ''"
              :elements="elements || []"
              :multiselect="multiselect"
              :required="required"
              @change="onChange"
              @close="close"
              @clear-selection="$emit('clear-selection')"
            />
          </slot>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts" generic="T extends SelectElement">
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

import ButtonFi from '@/components/atoms/button-fi/ButtonFi.vue';
import FacetListFi from '@/components/atoms/facet-list-fi/FacetListFi.vue';
import IconFi from '@/components/atoms/icon-fi/IconFi.vue';
import { bodyDisableScrollBody, bodyEnableScrollBody } from '@/composables/bodyScroll';
import { type SizeBreakpoint, useBreakpoints } from '@/composables/breakpoint';
import useComponentWrapper from '@/composables/componentWrapper';
import useJssFormsConditionalField from '@/composables/jssFormsConditionalField';

import type { SelectElement } from './select-fi-types';

interface Props {
  open: boolean;
  name?: string | null;
  elements?: T[] | null;
  multiselect?: boolean;
  required?: boolean;
  light?: boolean;
  small?: boolean;
  large?: boolean;
  largeFrom?: SizeBreakpoint;
  transparent?: boolean;
  disabled?: boolean;
  optionsOverlap?: boolean;
  mobileChevron?: boolean;
  noMobileSelection?: boolean;
  mobileInline?: boolean;
  forceOpenTop?: boolean;
  forceOpenBottom?: boolean;
  hasError?: boolean;
  chevronClosed?: string;
  chevronOpen?: string | null;
  dataTest2?: string | null;
  forceTheme?: string | null;
  dataFormElementKey?: string;
  dataFormItemId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  name: null,
  elements: null,
  multiselect: false,
  required: false,
  light: false,
  small: false,
  large: false,
  largeFrom: undefined,
  transparent: false,
  disabled: false,
  optionsOverlap: false,
  mobileChevron: false,
  noMobileSelection: false,
  mobileInline: false,
  forceOpenTop: false,
  forceOpenBottom: false,
  hasError: false,
  chevronClosed: 'chevron-down',
  chevronOpen: null,
  dataTest2: null,
  forceTheme: null,
  dataFormElementKey: undefined,
  dataFormItemId: undefined,
});

const emit = defineEmits<{
  change: [payload: { value: boolean; element: T }];
  'clear-selection': [];
  'toggle-open': [];
}>();

const { windowScrollPos, windowDimensions, isSM } = useBreakpoints();
const { wrapperClasses } = useComponentWrapper();
useJssFormsConditionalField(props);

const button = useTemplateRef('button');
const popout = useTemplateRef('popout');

const wrapperStyle = ref<Record<string, string>>({});
const internalOpen = ref(false);
const openTop = ref(false);
const popupPositioningEnabled = ref(false);
const isMounted = ref(false);
const bodyScrollDisabled = ref(false);
const leaving = ref(false);

const bodyDisableScroll = () => {
  bodyDisableScrollBody();
  bodyScrollDisabled.value = true;
};

const bodyEnableScroll = () => {
  if (!bodyScrollDisabled.value) {
    return;
  }
  bodyEnableScrollBody();
  bodyScrollDisabled.value = false;
};

const animationDuration = (): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, 300);
  });
};

const applyStyles = (): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, 10);
  });
};

const positionPopup = () => {
  if (IS_SSR || !isSM.value || !props.open || !button.value) {
    return;
  }

  const buttonBounding = button.value.getBoundingClientRect();
  const shadowWidth = 2 * 10;
  const top = openTop.value ? buttonBounding.top + 10 : buttonBounding.top + buttonBounding.height;
  wrapperStyle.value.top = `${top}px`;
  wrapperStyle.value.left = `${buttonBounding.left}px`;
  wrapperStyle.value.width = `${buttonBounding.width + shadowWidth}px`;
};

const closeIsSM = () => {
  if (isSM.value) {
    close();
  }
};

const close = () => {
  if (props.open) {
    onToggleOpen();
  }
};

const onToggleOpen = () => {
  emit('toggle-open');
};

const onChange = (payload: { value: boolean; element: SelectElement }) => {
  // Safe: FacetListFi always emits back the same object it received from elements (which is T at runtime).
  // Parameter is SelectElement because FacetListFi's internal FacetElement type is not exported.
  emit('change', payload as { value: boolean; element: T });
  if (!props.multiselect) {
    close();
  }
};

watch(
  () => props.open,
  async (value) => {
    if (IS_SSR) {
      return;
    }
    if (isSM.value) {
      bodyEnableScroll();
      const transitionMaxHeight = 250;
      const shadowMarginPx = 10;
      if (value) {
        internalOpen.value = true;
        openTop.value = false;
        wrapperStyle.value = {
          'max-height': '0px',
        };
        positionPopup();
        await applyStyles();
        if (popout.value) {
          const { height } = popout.value.getBoundingClientRect();
          const buttonBounding = button.value?.getBoundingClientRect() || { bottom: 0 };
          const { bottom: elementBottom } = buttonBounding;
          const position = elementBottom + window.document.documentElement.scrollTop;
          const { height: bodyHeight } = window.document.body.getBoundingClientRect();
          const popoutHeight = popout.value?.getBoundingClientRect()?.height || 0;
          const doesFitBelow = bodyHeight > position + popoutHeight;
          const targetHeight = height + shadowMarginPx;

          if (targetHeight > transitionMaxHeight) {
            wrapperStyle.value = {
              transition: 'none',
              opacity: '0',
              'max-height': `${targetHeight - transitionMaxHeight}px`,
            };
            positionPopup();
            await applyStyles();
          }
          wrapperStyle.value = {
            'max-height': `${targetHeight}px`,
          };
          positionPopup();
          await nextTick();
          if (!props.forceOpenBottom && (!doesFitBelow || props.forceOpenTop)) {
            openTop.value = true;
          }
          await animationDuration();
        }
      } else {
        if (popout.value) {
          const { height } = popout.value.getBoundingClientRect();
          const targetHeight = height + shadowMarginPx;
          wrapperStyle.value['max-height'] = `${targetHeight}px`;
          await applyStyles();

          if (targetHeight > transitionMaxHeight) {
            wrapperStyle.value['max-height'] = `${targetHeight - transitionMaxHeight}px`;
            wrapperStyle.value.opacity = '0';
          } else {
            wrapperStyle.value['max-height'] = '0px';
          }
          await animationDuration();
        }
        internalOpen.value = false;
        await nextTick();
      }
      wrapperStyle.value = {};
      positionPopup();
    } else {
      if (props.mobileInline) {
        internalOpen.value = value;
        return;
      }
      if (value) {
        leaving.value = false;
        bodyDisableScroll();
        internalOpen.value = value;
        wrapperStyle.value = {
          'max-height': '0px',
        };
        await applyStyles();
        if (popout.value) {
          const { height } = popout.value.getBoundingClientRect();

          wrapperStyle.value = {
            'max-height': `${height}px`,
          };
          await animationDuration();
        }
        wrapperStyle.value = {};
      } else {
        bodyEnableScroll();
        if (popout.value) {
          const { height } = popout.value.getBoundingClientRect();
          wrapperStyle.value = {
            'max-height': `${height}px`,
          };
          leaving.value = true;
          await applyStyles();
          wrapperStyle.value = {
            'max-height': '0px',
          };
          await animationDuration();
        }
        internalOpen.value = value;
        leaving.value = false;
      }
    }

    popupPositioningEnabled.value = value;
  },
  { immediate: true },
);

watch(
  () => windowScrollPos.value,
  () => {
    if (popupPositioningEnabled.value) {
      positionPopup();
    }
  },
  { deep: true },
);

watch(
  () => windowDimensions.value,
  () => {
    if (popupPositioningEnabled.value) {
      positionPopup();
    }
  },
  { deep: true },
);

onMounted(() => {
  isMounted.value = true;
});

onBeforeUnmount(() => {
  bodyEnableScroll();
});

defineExpose({
  close,
});
</script>

<style scoped src="./select-fi" />
